import { useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/joy';
import { useVirtualizer } from '@tanstack/react-virtual';
import { RecipeCard } from '@/features/recipes/components/RecipeCard';
import { useRecipeGridColumns } from '@/features/recipes/hooks/useRecipeGridColumns';
import { VirtualRecipeRows } from '@/features/recipes/components/VirtualRecipeRows';
import { useRecipeRows } from '@/features/recipes/hooks/useRecipeRows';
import AddIcon from '@mui/icons-material/Add';

// Searching, sorting and filtering (including which meal-type category applies)
// happen in AddMealModal, which owns those controls -- this component just
// renders the already-filtered list.
//
// Virtualized against the scroll box below rather than the window, because the
// list scrolls inside the modal. The recipes page uses the window-scrolled
// RecipeGrid instead; both draw their rows with VirtualRecipeRows.
export function RecipePickerList({ recipes, favoriteIds, onAdd, onAddNewRecipe }) {
  const { columns, gap, isMobile } = useRecipeGridColumns();
  const scrollRef = useRef(null);

  const rows = useRecipeRows(recipes, columns);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 300,
    overscan: 3,
  });

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', mx: -2.5, mb: -2, borderBottomLeftRadius: 'var(--joy-radius-sm)', borderBottomRightRadius: 'var(--joy-radius-sm)' }}>
        <Box sx={{ backgroundColor: 'var(--joy-palette-neutral-100)', p: { xs: 1, sm: 2.75 }, minHeight: '100%' }}>
          {recipes.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'var(--joy-palette-neutral-400)', textAlign: 'center', pt: 6 }}>
              No recipes found
            </Typography>
          ) : (
            <VirtualRecipeRows
              virtualizer={virtualizer}
              rows={rows}
              columns={columns}
              gap={gap}
              isMobile={isMobile}
              card={(recipe, cardIsMobile) => (
                <Box onClick={() => onAdd(recipe.id)} sx={{ cursor: 'pointer', minWidth: 0, flex: 1, display: 'flex' }}>
                  <RecipeCard recipe={recipe} clickable={false} isFavorite={favoriteIds.has(recipe.id)} isMobile={cardIsMobile} />
                </Box>
              )}
            />
          )}

          <AddNewRecipePrompt onClick={onAddNewRecipe} />
        </Box>
      </Box>
    </Box>
  );
}

// Sits at the bottom of the list -- below the grid when there are results (so
// it's reachable by scrolling to the end), or right under "No recipes found"
// when there aren't -- so it's the same fallback either way, not two
// different messages for what's really one situation.
function AddNewRecipePrompt({ onClick }) {
  return (
    <Stack direction="column" alignItems="center" gap={1} sx={{ pt: 4, pb: 1 }}>
      <Typography level="body-sm" sx={{ color: 'var(--joy-palette-neutral-500)' }}>
        Not finding what you&apos;re looking for?
      </Typography>
      <Button variant="outlined" color="neutral" onClick={onClick} startDecorator={<AddIcon />}>
        Add a new recipe
      </Button>
    </Stack>
  );
}
